import { useEffect, useMemo, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { LAP_COLORS } from '../LapSelector/LapSelector';
import { getDisplayUnit, makeValueTransform } from '../../utils/unitConvert';
import styles from './ReadoutsPanel.module.css';
import { lastCursor } from '../../utils/chartCursor';

const M_TO_FT = 3.28084;

function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(points[0].dist_ft - distFt);
  for (let i = 1; i < points.length; i++) {
    const diff = Math.abs(points[i].dist_ft - distFt);
    if (diff < bestDiff) { best = points[i]; bestDiff = diff; }
  }
  return best;
}

function fmtVal(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return Math.round(v).toLocaleString();
  if (abs >= 100)  return Math.round(v).toString();
  if (abs >= 10)   return v.toFixed(1);
  return v.toFixed(2);
}

// Build per-lap {points, min, max, avg} for one channel, applying the unit
// transform so the bar + number read in the user's display units.
function buildLapStat(trace, transform) {
  const raw = (trace?.points || []).filter(p => p.dist_m != null && p.channel_value != null);
  if (!raw.length) return null;
  let min = Infinity, max = -Infinity;
  const points = raw.map(p => {
    const v = transform ? transform(p.channel_value) : p.channel_value;
    if (v < min) min = v;
    if (v > max) max = v;
    return { dist_ft: p.dist_m * M_TO_FT, value: v };
  });
  return { points, min, max };
}

// Resolve each lap's current value (live at cursor, else lap max) + bar fraction.
function resolveLaps(lapStats, lapMeta, distFt, chMin, span) {
  return lapStats.map((s, i) => {
    if (!s) return null;
    const live = distFt != null ? findNearest(s.points, distFt)?.value : null;
    // At rest (no cursor) show the lap's peak value for the channel.
    const val  = live != null ? live : s.max;
    const idle = live == null;
    const frac = span > 0 ? Math.max(0, Math.min(1, (val - chMin) / span)) : 0.5;
    return { ...lapMeta[i], val, idle, frac };
  }).filter(Boolean);
}

// ── One channel block (header + per-lap bars) ─────────────────────────────────
function ChannelReadout({
  ch, lapStats, lapMeta, distFt, displayUnit, onRemove,
  onDragStart, onDragEnter, onDrop, onDragEnd, isDragging, isOver,
}) {
  // Shared scale across all selected laps so readings are directly comparable.
  let chMin = Infinity, chMax = -Infinity;
  lapStats.forEach(s => { if (s) { chMin = Math.min(chMin, s.min); chMax = Math.max(chMax, s.max); } });
  const hasData = Number.isFinite(chMin) && Number.isFinite(chMax);
  const span = chMax - chMin;
  const laps = hasData ? resolveLaps(lapStats, lapMeta, distFt, chMin, span) : [];

  return (
    <div
      className={`${styles.channel} ${isDragging ? styles.dragging : ''} ${isOver ? styles.dragOver : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className={styles.chHeader}>
        <GripVertical size={13} className={styles.grip} aria-hidden />
        <span className={styles.chName} title={ch.name}>{ch.name}</span>
        <button className={styles.chRemove} title="Remove" onClick={() => onRemove(ch)}>×</button>
      </div>

      {!hasData ? (
        <div className={styles.chEmpty}>No data</div>
      ) : (
        <>
          {laps.map(l => (
            <div key={l.lapIndex} className={styles.row}>
              <span className={styles.lapDot} style={{ background: l.color }} />
              <div className={styles.track}>
                <div
                  className={styles.fill}
                  style={{ width: `${l.frac * 100}%`, background: l.color, opacity: l.idle ? 0.45 : 1 }}
                />
              </div>
              <span className={`${styles.num} ${l.idle ? styles.numIdle : ''}`}>
                {fmtVal(l.val)}
                {displayUnit && <span className={styles.numUnit}>{displayUnit}</span>}
              </span>
            </div>
          ))}
          <div className={styles.range}>
            {fmtVal(chMin)} – {fmtVal(chMax)}{displayUnit ? ` ${displayUnit}` : ''}
          </div>
        </>
      )}
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────
export default function ReadoutsPanel({
  channels, channelTracesMap, selectedLaps,
  lapTraces = {}, customColors1 = null, unitPrefs,
  onRemoveChannel, onReorder,
}) {
  const [distFt, setDistFt] = useState(null);
  const [dragName, setDragName] = useState(null);
  const [overName, setOverName] = useState(null);

  // Drop `dragName` into `overName`'s slot, emitting the new channel order.
  const commitReorder = () => {
    if (dragName && overName && dragName !== overName) {
      const names = channels.map(c => c.name).filter(n => n !== dragName);
      const idx = names.indexOf(overName);
      names.splice(idx < 0 ? names.length : idx, 0, dragName);
      onReorder?.(names);
    }
    setDragName(null);
    setOverName(null);
  };

  // Follow the shared cursor bus (same events SpeedTrace/ChannelTrace use).
  useEffect(() => {
    const onHover = (e) => {
      const d = e.detail?.distFt;
      setDistFt(d == null ? null : d);
    };
    window.addEventListener('trackapp:chart-hover', onHover);
    window.addEventListener('trackapp:map-hover', onHover);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) onHover({ detail: last });
    return () => {
      window.removeEventListener('trackapp:chart-hover', onHover);
      window.removeEventListener('trackapp:map-hover', onHover);
    };
  }, []);

  // Lap color + label metadata (mirrors ChannelTrace's series ordering).
  const lapMeta = useMemo(() => selectedLaps.map((lapIndex, i) => ({
    lapIndex,
    color: (customColors1 && customColors1[lapIndex]) || LAP_COLORS[i % LAP_COLORS.length],
    label: lapTraces[lapIndex]?.lap_time_str || `L${lapIndex}`,
  })), [selectedLaps, customColors1, lapTraces]);

  // Per-channel, per-lap stats — recomputed only when traces/units change.
  const statsByChannel = useMemo(() => {
    const out = {};
    channels.forEach(ch => {
      const transform = makeValueTransform(ch.unit, unitPrefs);
      const perLap = (channelTracesMap[ch.name] || {});
      out[ch.name] = selectedLaps.map(lapIndex => buildLapStat(perLap[lapIndex], transform));
    });
    return out;
  }, [channels, channelTracesMap, selectedLaps, unitPrefs]);

  if (!channels.length) {
    return <div className={styles.placeholder}>Pick channels' “Value” box to add readouts.</div>;
  }

  return (
    <div className={styles.body}>
      {channels.map(ch => (
        <ChannelReadout
          key={ch.name}
          ch={ch}
          lapStats={statsByChannel[ch.name] || []}
          lapMeta={lapMeta}
          distFt={distFt}
          displayUnit={getDisplayUnit(ch.unit, unitPrefs)}
          onRemove={onRemoveChannel}
          onDragStart={() => setDragName(ch.name)}
          onDragEnter={() => dragName && setOverName(ch.name)}
          onDrop={commitReorder}
          onDragEnd={() => { setDragName(null); setOverName(null); }}
          isDragging={dragName === ch.name}
          isOver={overName === ch.name && dragName !== ch.name}
        />
      ))}
    </div>
  );
}
